import { useState, useEffect, useCallback } from 'react';
import adsService from '../utils/services/AdsService';

/**
 * Custom hook for AdsService functionality
 * Provides easy access to ads operations with loading states and error handling
 */
export const useAdsService = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Clear error when component unmounts or when explicitly called
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Get all ads (admin only)
  const getAllAds = useCallback(async (useCache = true) => {
    try {
      setLoading(true);
      setError(null);
      const ads = await adsService.getAllAds(useCache);
      return ads;
    } catch (err) {
      console.error('Error in getAllAds:', err);
      setError(err.message || 'Failed to load ads');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Get user's ads
  const getUserAds = useCallback(async (uid, useCache = true) => {
    try {
      setLoading(true);
      setError(null);
      const ads = await adsService.getUserAds(uid, useCache);
      return ads;
    } catch (err) {
      console.error('Error in getUserAds:', err);
      setError(err.message || 'Failed to load user ads');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Get a single ad
  const getAd = useCallback(async (adKey, useCache = true) => {
    try {
      setLoading(true);
      setError(null);
      const ad = await adsService.getAd(adKey, useCache);
      return ad;
    } catch (err) {
      console.error('Error in getAd:', err);
      setError(err.message || 'Failed to load ad');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Create a new ad
  const createAd = useCallback(async (adData, imageObject, imagePortObject) => {
    try {
      setLoading(true);
      setError(null);
      const ad = await adsService.createAd(adData, imageObject, imagePortObject);
      return ad;
    } catch (err) {
      console.error('Error in createAd:', err);
      setError(err.message || 'Failed to create ad');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Modify an existing ad
  const modifyAd = useCallback(async (adKey, adData, imageObject, imagePortObject) => {
    try {
      setLoading(true);
      setError(null);
      const ad = await adsService.modifyAd(adKey, adData, imageObject, imagePortObject);
      return ad;
    } catch (err) {
      console.error('Error in modifyAd:', err);
      setError(err.message || 'Failed to modify ad');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Delete an ad
  const deleteAd = useCallback(async (adKey) => {
    try {
      setLoading(true);
      setError(null);
      await adsService.deleteAd(adKey);
    } catch (err) {
      console.error('Error in deleteAd:', err);
      setError(err.message || 'Failed to delete ad');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);





  // Record ad hit
  const hitAd = useCallback(async (adKey) => {
    try {
      setError(null);
      await adsService.hitAd(adKey);
    } catch (err) {
      console.error('Error in hitAd:', err);
      setError(err.message || 'Failed to record ad hit');
      throw err;
    }
  }, []);





  // Toggle ad active status
  const toggleAdActive = useCallback(async (adKey, active) => {
    try {
      setLoading(true);
      setError(null);
      await adsService.toggleAdActive(adKey, active);
    } catch (err) {
      console.error('Error in toggleAdActive:', err);
      setError(err.message || 'Failed to toggle ad active status');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Get active ads
  const getActiveAds = useCallback(async (useCache = true) => {
    try {
      setLoading(true);
      setError(null);
      const ads = await adsService.getActiveAds(useCache);
      return ads;
    } catch (err) {
      console.error('Error in getActiveAds:', err);
      setError(err.message || 'Failed to load active ads');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Get ads statistics
  const getAdsStats = useCallback(async (useCache = true) => {
    try {
      setLoading(true);
      setError(null);
      const stats = await adsService.getAdsStats(useCache);
      return stats;
    } catch (err) {
      console.error('Error in getAdsStats:', err);
      setError(err.message || 'Failed to load ads statistics');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Get ads by date range
  const getAdsByDateRange = useCallback(async (startDate, endDate, useCache = true) => {
    try {
      setLoading(true);
      setError(null);
      const ads = await adsService.getAdsByDateRange(startDate, endDate, useCache);
      return ads;
    } catch (err) {
      console.error('Error in getAdsByDateRange:', err);
      setError(err.message || 'Failed to load ads by date range');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    // State
    loading,
    error,
    
    // Actions
    clearError,
    getAllAds,
    getUserAds,
    getAd,
    createAd,
    modifyAd,
    deleteAd,
    hitAd,
    toggleAdActive,
    getActiveAds,
    getAdsStats,
    getAdsByDateRange,
    
    // Service instance for advanced usage
    service: adsService
  };
};

/**
 * Hook for managing ads list with real-time updates
 */
export const useAdsList = (filters = {}, useCache = true) => {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { getAllAds, getUserAds } = useAdsService();

  const loadAds = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      let adsData;
      if (filters.uid) {
        adsData = await getUserAds(filters.uid, useCache);
      } else {
        adsData = await getAllAds(useCache);
      }
      
      setAds(adsData);
    } catch (err) {
      console.error('Error loading ads list:', err);
      setError(err.message || 'Failed to load ads');
    } finally {
      setLoading(false);
    }
  }, [filters.uid, useCache, getAllAds, getUserAds]);

  // Subscribe to real-time updates
  useEffect(() => {
    const unsubscribe = adsService.subscribeToAds((updatedAds) => {
      setAds(updatedAds);
    }, filters);

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [filters]);

  // Initial load
  useEffect(() => {
    loadAds();
  }, [loadAds]);

  return {
    ads,
    loading,
    error,
    reload: loadAds
  };
};

/**
 * Hook for managing a single ad with real-time updates
 */
export const useAd = (adKey, useCache = true) => {
  const [ad, setAd] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { getAd } = useAdsService();

  const loadAd = useCallback(async () => {
    if (!adKey) {
      setAd(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const adData = await getAd(adKey, useCache);
      setAd(adData);
    } catch (err) {
      console.error('Error loading ad:', err);
      setError(err.message || 'Failed to load ad');
    } finally {
      setLoading(false);
    }
  }, [adKey, useCache, getAd]);

  // Subscribe to real-time updates
  useEffect(() => {
    if (!adKey) return;

    const unsubscribe = adsService.subscribeToDocument(adKey, (updatedAd) => {
      setAd(updatedAd);
    });

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [adKey]);

  // Initial load
  useEffect(() => {
    loadAd();
  }, [loadAd]);

  return {
    ad,
    loading,
    error,
    reload: loadAd
  };
}; 